/**
 * Shifts, handovers and payouts.
 *
 * Every mutation here invalidates `cashPositionTag`, because every one of them changes what
 * somebody is holding — and the cash-in-hand screen is the one place that has to be right on a
 * Monday morning when the question is who has been carrying sixty thousand shillings since Friday.
 */

import { api } from '../api.js';
import { cashPositionTag, handoverListTag, shiftListTag, shiftTag } from '../tags.js';
import type { Paged, ShopId, UserId } from '../../types/api.js';
import type {
  CashHandover,
  CashInHand,
  CashPayout,
  DeclareShiftBody,
  HandoverId,
  HandoverRecipient,
  HandoverRecipientKind,
  PayoutId,
  PayoutReasonCode,
  Shift,
  ShiftId,
  ShiftStatus,
  VarianceApproval,
  VarianceHistory,
} from '../../types/payments.js';

export const cashApi = api.injectEndpoints({
  endpoints: (build) => ({
    // ── Shifts ──────────────────────────────────────────────────────────────
    /**
     * The caller's own open shift, or `null`.
     *
     * `null` is a normal answer, not an error: taking cash without an open shift is allowed. It
     * is simply unattributed, and the till says so rather than blocking the sale.
     */
    getCurrentShift: build.query<Shift | null, ShopId | undefined>({
      // Conditional spread rather than a possibly-undefined `params`: under
      // exactOptionalPropertyTypes, "the key is absent" and "the key is undefined" are
      // different types, and RTK's FetchArgs only accepts the first.
      query: (shopId) => ({ url: '/shifts/current', ...(shopId ? { params: { shopId } } : {}) }),
      providesTags: (result) => (result ? [shiftTag(result.id), 'CurrentShift'] : ['CurrentShift']),
    }),

    openShift: build.mutation<Shift, { shopId: ShopId; note?: string | null }>({
      query: (body) => ({ url: '/shifts', method: 'POST', body }),
      invalidatesTags: (_result, _error, { shopId }) => [
        'CurrentShift',
        shiftListTag(shopId),
        cashPositionTag(shopId),
      ],
    }),

    getShift: build.query<Shift, ShiftId>({
      query: (id) => `/shifts/${id}`,
      providesTags: (_result, _error, id) => [shiftTag(id)],
    }),

    listShifts: build.query<
      Paged<Shift>,
      {
        shopId: ShopId;
        userId?: UserId | undefined;
        status?: ShiftStatus | undefined;
        approvalStatus?: VarianceApproval | undefined;
        from?: string | undefined;
        to?: string | undefined;
        page?: number | undefined;
        pageSize?: number | undefined;
      }
    >({
      query: (params) => ({ url: '/shifts', params: { page: 1, pageSize: 25, ...params } }),
      providesTags: (result, _error, { shopId }) => [
        shiftListTag(shopId),
        ...(result?.items ?? []).map((shift) => shiftTag(shift.id)),
      ],
    }),

    declareShift: build.mutation<Shift, { shiftId: ShiftId; shopId: ShopId; body: DeclareShiftBody }>({
      query: ({ shiftId, body }) => ({ url: `/shifts/${shiftId}/declare`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { shiftId, shopId }) => [
        shiftTag(shiftId),
        'CurrentShift',
        shiftListTag(shopId),
        cashPositionTag(shopId),
        // The declaration creates the handover, so the receiving manager's inbox changes too.
        handoverListTag(shopId),
      ],
    }),

    approveVariance: build.mutation<
      Shift,
      { shiftId: ShiftId; shopId: ShopId; approve: boolean; note?: string | null }
    >({
      query: ({ shiftId, approve, note }) => ({
        url: `/shifts/${shiftId}/approve-variance`,
        method: 'POST',
        body: { approve, note },
      }),
      invalidatesTags: (_result, _error, { shiftId, shopId }) => [
        shiftTag(shiftId),
        shiftListTag(shopId),
        cashPositionTag(shopId),
      ],
    }),

    getVarianceHistory: build.query<
      VarianceHistory,
      { shopId: ShopId; from?: string | undefined; to?: string | undefined }
    >({
      query: (params) => ({ url: '/cash/variance-history', params }),
      providesTags: (_result, _error, { shopId }) => [shiftListTag(shopId)],
    }),

    // ── Cash position ───────────────────────────────────────────────────────
    getCashInHand: build.query<CashInHand, ShopId>({
      query: (shopId) => ({ url: '/cash/in-hand', params: { shopId } }),
      providesTags: (_result, _error, shopId) => [cashPositionTag(shopId)],
    }),

    // ── Handovers ───────────────────────────────────────────────────────────
    listHandovers: build.query<
      Paged<CashHandover>,
      { shopId: ShopId; status?: string | undefined; forMe?: boolean | undefined }
    >({
      query: (params) => ({ url: '/handovers', params: { pageSize: 50, ...params } }),
      providesTags: (_result, _error, { shopId }) => [handoverListTag(shopId)],
    }),

    /**
     * Who at this shop can actually confirm a handover — the "who is taking it?" list. Not the
     * same as any user list: a salesperson can call this without `user.view`, and it only ever
     * names people who hold `handover.receive`, at this shop, excluding the caller.
     */
    listHandoverRecipients: build.query<HandoverRecipient[], { shopId: ShopId }>({
      query: ({ shopId }) => ({ url: '/handovers/recipients', params: { shopId } }),
    }),

    declareHandover: build.mutation<
      CashHandover,
      {
        shopId: ShopId;
        shiftId?: ShiftId | null;
        toKind: HandoverRecipientKind;
        toUserId?: UserId | null;
        amountKes: number;
        slipReference?: string | null;
        note?: string | null;
      }
    >({
      query: (body) => ({ url: '/handovers', method: 'POST', body }),
      invalidatesTags: (_result, _error, { shopId }) => [
        handoverListTag(shopId),
        cashPositionTag(shopId),
        'CurrentShift',
      ],
    }),

    /** The receiver, from their own login. This one call is the whole two-party control. */
    confirmHandover: build.mutation<
      CashHandover,
      { handoverId: HandoverId; shopId: ShopId; confirmedAmountKes: number; disputeReason?: string | null }
    >({
      query: ({ handoverId, confirmedAmountKes, disputeReason }) => ({
        url: `/handovers/${handoverId}/confirm`,
        method: 'POST',
        body: { confirmedAmountKes, disputeReason },
      }),
      invalidatesTags: (result, _error, { shopId }) => [
        handoverListTag(shopId),
        cashPositionTag(shopId),
        ...(result?.shiftId ? [shiftTag(result.shiftId)] : []),
      ],
    }),

    cancelHandover: build.mutation<CashHandover, { handoverId: HandoverId; shopId: ShopId }>({
      query: ({ handoverId }) => ({ url: `/handovers/${handoverId}/cancel`, method: 'POST' }),
      invalidatesTags: (_result, _error, { shopId }) => [
        handoverListTag(shopId),
        cashPositionTag(shopId),
        'CurrentShift',
      ],
    }),

    // ── Payouts ─────────────────────────────────────────────────────────────
    listPayouts: build.query<
      Paged<CashPayout>,
      {
        shopId: ShopId;
        shiftId?: ShiftId | undefined;
        reasonCode?: PayoutReasonCode | undefined;
        from?: string | undefined;
        to?: string | undefined;
        includeVoid?: boolean | undefined;
      }
    >({
      query: (params) => ({ url: '/cash/payouts', params: { pageSize: 50, ...params } }),
      providesTags: (_result, _error, { shopId }) => [cashPositionTag(shopId)],
    }),

    recordPayout: build.mutation<
      CashPayout,
      {
        shopId: ShopId;
        shiftId?: ShiftId | null;
        amountKes: number;
        reasonCode: PayoutReasonCode;
        reasonNote?: string | null;
        payee?: string | null;
        receiptReference?: string | null;
      }
    >({
      query: (body) => ({ url: '/cash/payouts', method: 'POST', body }),
      invalidatesTags: (_result, _error, { shopId }) => [cashPositionTag(shopId), 'CurrentShift'],
    }),

    voidPayout: build.mutation<CashPayout, { payoutId: PayoutId; shopId: ShopId; reason: string }>({
      query: ({ payoutId, reason }) => ({
        url: `/cash/payouts/${payoutId}/void`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (_result, _error, { shopId }) => [cashPositionTag(shopId), 'CurrentShift'],
    }),
  }),
});

export const {
  useGetCurrentShiftQuery,
  useOpenShiftMutation,
  useGetShiftQuery,
  useListShiftsQuery,
  useDeclareShiftMutation,
  useApproveVarianceMutation,
  useGetVarianceHistoryQuery,
  useGetCashInHandQuery,
  useListHandoversQuery,
  useListHandoverRecipientsQuery,
  useDeclareHandoverMutation,
  useConfirmHandoverMutation,
  useCancelHandoverMutation,
  useListPayoutsQuery,
  useRecordPayoutMutation,
  useVoidPayoutMutation,
} = cashApi;
